import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import type { BoardQueue } from '@stitch/pbx-adapter';
import { Icon } from '../lib/icons';
import { afterHours, apartShort, dayThereIfDifferent, isZone, minutesApart, shortZoneName, timeIn, zoneName } from '../lib/time';
import { hueOf } from './messages/Avatar';
import { useLessMotion, useMarquee } from './messages/LocalTime';

/** People in a time zone, from the TV's board (initials only when the TV shows agents' names). */
export interface TeamZone {
  zone: string;
  count: number;
  initials: string[];
}

interface Since {
  /** The hour it was taken, on the company's clock ("2026-10-28T11"). */
  hour: string;
  /** When it was taken (ms): a TV switched on mid-hour counts from then, and says so (3136). */
  at?: number;
  waiting: number;
  answered: Record<string, number>;
}

const sinceKey = (id: string) => `stitch.tv.${id}.since`;
function readSince(id: string): Since | null {
  try {
    const v = JSON.parse(localStorage.getItem(sinceKey(id)) ?? 'null') as Since | null;
    return v && typeof v.hour === 'string' && typeof v.waiting === 'number' && v.answered && typeof v.answered === 'object' ? v : null;
  } catch {
    return null;
  }
}
function keepSince(id: string, s: Since): void {
  try {
    localStorage.setItem(sinceKey(id), JSON.stringify(s));
  } catch {
    // Private mode: kept until the window is closed.
  }
}

/** "2026-10-28T11": the hour now on a clock. */
function hourIn(zone: string, now: number): string {
  try {
    const p = Object.fromEntries(
      new Intl.DateTimeFormat('en-CA', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23' })
        .formatToParts(new Date(now))
        .map((x) => [x.type, x.value])
    );
    return `${p.year}-${p.month}-${p.day}T${p.hour}`;
  } catch {
    return new Date(now).toISOString().slice(0, 13);
  }
}

/** "11 AM": an hour from `hourIn`. */
function hourWords(hour: string): string {
  const h = Number(hour.slice(11, 13));
  return `${h % 12 || 12} ${h < 12 ? 'AM' : 'PM'}`;
}

const ready = (q: BoardQueue) => q.agents.filter((a) => a.state === 'available').length;

/**
 * Changes since the top of the hour (the company's), kept on this TV so a reload doesn't lose them.
 * Taken again when the hour turns, which also starts the day over at midnight.
 */
export function useSinceTheHour(id: string, zone: string, queues: BoardQueue[] | undefined, now: number): Since | null {
  const hour = hourIn(zone, now);
  const [since, setSince] = useState<Since | null>(() => readSince(id));
  useEffect(() => {
    if (!queues) return;
    if (since?.hour === hour) return;
    const next: Since = { hour, at: now, waiting: queues.reduce((n, q) => n + q.waiting, 0), answered: Object.fromEntries(queues.map((q) => [q.extension, q.answeredToday ?? 0])) };
    keepSince(id, next);
    setSince(next);
  }, [id, hour, queues, since?.hour]);
  return since?.hour === hour ? since : null;
}

/**
 * The TV's ticker along the bottom (1.12.13), on Stitch's dark banner: the company's clock at the
 * left, then queues with callers waiting and nobody ready (in red, first), the queue numbers that
 * changed since the top of the hour, and the time where the company's people are. It moves only when
 * it doesn't fit, and stays still when the TV asks for less motion. The queue tiles above keep every
 * number and warning; this is the at-a-glance line.
 */
export function TvTicker({ id, company, companyZone, queues, team, now }: { id: string; company: string; companyZone: string | undefined; queues: BoardQueue[]; team: TeamZone[] | undefined; now: number }) {
  const zone = companyZone && isZone(companyZone) ? companyZone : Intl.DateTimeFormat().resolvedOptions().timeZone;
  const since = useSinceTheHour(id, zone, queues, now);
  const at = new Date(now);
  const lessMotion = useLessMotion();

  const alerts = queues.filter((q) => q.waiting > 0 && ready(q) === 0);
  const waiting = queues.reduce((n, q) => n + q.waiting, 0);
  // Zones with the same time now are one stop; the company's own time is at the left already.
  const stops = useMemo(() => {
    const by = new Map<number, TeamZone & { apart: number }>();
    for (const t of team ?? []) {
      if (!isZone(t.zone)) continue;
      const apart = minutesApart(t.zone, zone);
      if (apart === 0) continue;
      const s = by.get(apart) ?? { zone: t.zone, count: 0, initials: [], apart };
      s.count += t.count;
      s.initials = [...s.initials, ...t.initials];
      by.set(apart, s);
    }
    return [...by.values()].sort((a, b) => a.apart - b.apart);
  }, [team, zone]);

  const change = (n: number, worseUp: boolean | null) => {
    const tone = n === 0 || worseUp === null ? 'same' : (n > 0) === worseUp ? 'worse' : 'better';
    return (
      <span className={`q-ch q-${tone}`}>
        {n === 0 ? '±0' : <><span aria-hidden="true">{n > 0 ? '▲' : '▼'}</span>{Math.abs(n)}</>}
      </span>
    );
  };
  // From the top of the hour, or, when this TV first read the board partway through it, from then:
  // "since 2:37 PM", not "since 2 PM" (3136).
  const midHour = since?.at !== undefined && hourIn(zone, since.at) === since.hour && Number(timeIn(zone, new Date(since.at)).split(':')[1]?.slice(0, 2) ?? 0) > 1;
  const sinceWords = since ? `since ${midHour ? timeIn(zone, new Date(since.at!)) : hourWords(since.hour)}` : '';

  const items = (copy: boolean) => (
    <>
      {alerts.map((q) => (
        <span key={`a${q.extension}`} className="tk-item">
          <span className="q-alert" aria-hidden={copy || undefined}>
            <Icon name="alert" size={20} /> {q.name}: callers waiting, nobody ready
          </span>
          <span className="tk-sep" aria-hidden="true" />
        </span>
      ))}
      <span className="tk-item">
        <span className="q-stop" aria-hidden={copy || undefined} aria-label={copy ? undefined : `Waiting now: ${waiting}${since ? `, ${waiting - since.waiting >= 0 ? 'up' : 'down'} ${Math.abs(waiting - since.waiting)} ${sinceWords}` : ''}`}>
          <span className="tk-zone">Waiting now</span>
          <span className="q-n">{waiting}</span>
          {since && change(waiting - since.waiting, true)}
        </span>
        <span className="tk-sep" aria-hidden="true" />
      </span>
      {queues
        .filter((q) => q.answeredToday !== undefined)
        .map((q) => {
          const d = since ? (q.answeredToday ?? 0) - (since.answered[q.extension] ?? q.answeredToday ?? 0) : 0;
          return (
            <span key={`q${q.extension}`} className="tk-item">
              <span className="q-stop" aria-hidden={copy || undefined} aria-label={copy ? undefined : `${q.name} answered today: ${q.answeredToday}${since ? `, ${d} ${sinceWords}` : ''}`}>
                <span className="tk-zone">{q.name} answered</span>
                <span className="q-n">{q.answeredToday}</span>
                {since && change(d, null)}
              </span>
              <span className="tk-sep" aria-hidden="true" />
            </span>
          );
        })}
      {stops.map((s) => {
        const late = afterHours(s.zone, at);
        const day = dayThereIfDifferent(s.zone, zone, at);
        const t = timeIn(s.zone, at);
        return (
          <span key={`z${s.apart}`} className="tk-item">
            <span
              className={`tk-stop${late ? ' tk-late' : ''}`}
              aria-hidden={copy || undefined}
              aria-label={copy ? undefined : `${zoneName(s.zone)}: ${t}${day ? ` ${day}` : ''}, ${s.count === 1 ? '1 person' : `${s.count} people`}`}
            >
              <span className="tk-zone">{shortZoneName(s.zone)}</span>
              {late && <Icon name="moon" size={18} />}
              <span className="tk-time">
                {t.replace(/ ?(AM|PM)$/, '')}
                <span className="tk-ampm">{/PM$/.test(t) ? 'PM' : 'AM'}</span>
              </span>
              {day && <span className="tk-day">{day}</span>}
              <span className="tk-delta">
                <span aria-hidden="true">{s.apart > 0 ? '▲' : '▼'}</span>
                {apartShort(s.apart)}
              </span>
              <span className="tk-faces">
                {s.initials.slice(0, 3).map((i, n) => (
                  <span key={n} className="tk-face" style={{ '--msg-hue': hueOf(`${s.zone}${i}${n}`) } as CSSProperties}>
                    {i}
                  </span>
                ))}
                {s.count > Math.min(3, s.initials.length) && <span className="tk-face tk-more">{s.initials.length ? `+${s.count - Math.min(3, s.initials.length)}` : s.count}</span>}
              </span>
            </span>
            <span className="tk-sep" aria-hidden="true" />
          </span>
        );
      })}
    </>
  );

  const { windowRef, runRef, fits, width } = useMarquee<HTMLDivElement, HTMLSpanElement>([alerts.length, stops.length, queues.length]);
  const marquee = !fits && !lessMotion;
  const t = timeIn(zone, at);
  return (
    <div className={`tk tv-tk${marquee ? ' tk-marquee' : ''}`} role="region" aria-label={`Ticker${sinceWords ? `, changes ${sinceWords}` : ''}`}>
      <div className="tk-you">
        <span className="tk-you-tag">{company.toUpperCase()}</span>
        <span className="tv-tk-clock">
          <span className="tk-time">
            {t.replace(/ ?(AM|PM)$/, '')}
            <span className="tk-ampm">{/PM$/.test(t) ? 'PM' : 'AM'}</span>
          </span>
          <span className="tk-zone">{shortZoneName(zone)}</span>
        </span>
      </div>
      <div className="tk-window" ref={windowRef}>
        <div className="tk-track" style={marquee ? ({ '--tk-run': `${Math.max(30, Math.round(width / 45))}s` } as CSSProperties) : undefined}>
          <span className="tk-run" ref={runRef}>
            {items(false)}
          </span>
          {marquee && (
            <span className="tk-run" aria-hidden="true">
              {items(true)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

/** "Ticker: change since 11 AM", for the top of the TV. */
export function tickerNote(zone: string | undefined, now: number): string {
  const z = zone && isZone(zone) ? zone : Intl.DateTimeFormat().resolvedOptions().timeZone;
  return `Ticker: change since ${hourWords(hourIn(z, now))}`;
}
